import {
  AuthenticateWithRedirectCallback,
  useAuth,
  useSignIn,
  useSignUp,
} from "@clerk/clerk-react";
import type { SetActiveParams, SignInResource, SignInStatus } from "@clerk/types";
import { Result } from "@swan-io/boxed";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";
import { hasDefinedKeys, useForm } from "react-ux-form";
import { P, match } from "ts-pattern";
import { Box } from "../components/Box";
import { Button, LogWithButton } from "../components/Button";
import { FullScreenScrollView } from "../components/FullScreenScrollView";
import { Logo } from "../components/Icon";
import { InputOtp } from "../components/InputOtp";
import { InputText } from "../components/InputText";
import { Redirect } from "../components/Redirect";
import { Space } from "../components/Space";
import { Text } from "../components/Text";
import { Tile } from "../components/Tile";
import { toast } from "../components/Toast";
import { useTitle } from "../hooks/useTitle";
import { ApplicationKey } from "../utils/applications";
import { colors, invariantColor } from "../utils/design";
import { fetch, graphql, usePromiseMutation } from "../utils/graphql";
import { locale, t } from "../utils/i18n";
import { router } from "../utils/routes";
import { validateEmail } from "../utils/validation";
import { LoadingPage } from "./LoadingPage";

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 36,
    paddingVertical: 36,
    minHeight: 500,
  },

  orDivider: {
    height: 1,
    flex: 1,
    backgroundColor: colors.neutral[700],
  },
});

const signUpUser = async (email: string): Promise<Result<number, unknown>> => {
  // remove __session cookie in case user signup without signout
  // otherwise, the backend app will try to authenticate the request with an outdated session token
  Cookies.remove("__session");

  const document = graphql(`
    mutation Signup($email: Email!, $locale: Locale!) {
      signup(email: $email, locale: $locale) {
        id
      }
    }
  `);

  return Result.fromPromise(
    fetch({
      document,
      variables: {
        email,
        locale: locale.toLocaleUpperCase() as Uppercase<typeof locale>,
      },
    }).then(({ signup }) => signup.id),
  );
};

const signInUser = async (
  ressource: SignInResource,
  email: string,
  hasSignUp = false,
): Promise<Result<string, unknown>> => {
  try {
    const result = await ressource.create({
      strategy: "email_code",
      identifier: email,
    });

    if (result.status === "needs_first_factor") {
      return Result.Ok(email);
    }

    throw new Error(result.status ?? "unknown");
  } catch (error) {
    // If signup succeed, no need to signup again
    if (hasSignUp) {
      toast({
        variant: "error",
        title: t("signin.error.unexpected.title"),
        message: t("signin.error.unexpected.description"),
      });
      return Result.Error(error);
    }

    const signUpResult = await signUpUser(email);

    return signUpResult.match({
      Ok: () => signInUser(ressource, email, true),
      Error: (error) => Promise.resolve(Result.Error(error)),
    });
  }
};

const sendOtpCode =
  (ressource: SignInResource, setActive: (params: SetActiveParams) => Promise<void>) =>
  async (otp: string): Promise<Result<string, SignInStatus | "unknown">> => {
    try {
      const result = await ressource.attemptFirstFactor({
        strategy: "email_code",
        code: otp,
      });

      const { status, createdSessionId } = result;

      if (status !== "complete" || createdSessionId == null) {
        toast({
          variant: "error",
          title: t("signin_verify.error.invalid.title"),
          message: t("signin_verify.error.invalid.description"),
        });
        return Result.Error(status ?? "unknown");
      }

      await setActive({ session: createdSessionId });

      return Result.Ok(createdSessionId);
    } catch (error) {
      toast({
        variant: "error",
        title: t("signin_verify.error.invalid.title"),
        message: t("signin_verify.error.invalid.description"),
      });
      console.error(error);
      return Result.Error("unknown");
    }
  };

type OAuthSignInButtonProps = {
  provider: "google" | "microsoft" | "github";
  redirectTo: string | undefined;
  children: string;
};

const OAuthSignInButton: React.FC<OAuthSignInButtonProps> = ({
  provider,
  redirectTo,
  children,
}) => {
  const { signUp } = useSignUp();
  const { mutate: signIn, data: signInResult } = usePromiseMutation(async () => {
    try {
      const redirectUrlComplete = match(redirectTo)
        .with(P.nullish, () => window.location.origin)
        .with(P.string.startsWith("/"), (path) => window.location.origin + path)
        .with(P.string.startsWith("http"), (url) => url)
        .otherwise((path) => window.location.origin + "/" + path);

      await signUp?.authenticateWithRedirect({
        strategy: `oauth_${provider}`,
        redirectUrl: window.location.origin + router.SigninSsoCallback({ redirect_to: redirectTo }),
        redirectUrlComplete,
        unsafeMetadata: {
          locale,
        },
      });

      return Result.Ok(undefined);
    } catch (error) {
      console.error(error);
      toast({
        variant: "error",
        title: t("signin.error.unexpected.title"),
        message: t("signin.error.unexpected.description"),
      });
      return Result.Error(error);
    }
  });

  return (
    <LogWithButton provider={provider} status={signInResult} onPress={signIn}>
      {children}
    </LogWithButton>
  );
};

type EmailFormProps = {
  fromApp: ApplicationKey | undefined;
  signInRessource: SignInResource;
  oAuthredirectTo: string | undefined;
  onSuccess: (email: string) => void;
};

const EmailForm: React.FC<EmailFormProps> = ({
  fromApp,
  signInRessource,
  oAuthredirectTo,
  onSuccess,
}) => {
  const { mutate: signIn, data: signInResult } = usePromiseMutation((email: string) =>
    signInUser(signInRessource, email),
  );

  const { Field, submitForm } = useForm({
    email: {
      initialValue: "",
      validate: (value) => {
        if (validateEmail(value) != null) {
          return t("signin.error.invalid_email.title");
        }
      },
    },
  });

  const handleSubmit = () =>
    submitForm(
      async (values) => {
        if (hasDefinedKeys(values, ["email"])) {
          const result = await signIn(values.email);
          if (result.isOk()) {
            onSuccess(values.email);
          }
        }
      },
      ({ email }) => {
        if (email != null) {
          toast({
            variant: "error",
            title: t("signin.error.invalid_email.title"),
            message: t("signin.error.invalid_email.description"),
          });
        }
      },
    );

  return (
    <>
      {match(fromApp)
        .with("dedale", () => <Logo name="logo_dedale" width={108} height={26} />)
        .with(P.nullish, () => <Logo name="logo_type_bireme" width={107} height={20} />)
        .exhaustive()}

      <Space height={24} />

      <Text variant="body" color={invariantColor.white}>
        {t("signin.title")}
      </Text>

      <Space height={8} />
      <Text variant="small">{t("signin.description")}</Text>
      <Space height={24} />

      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <Field name="email">
          {({ value, error, onChange, onBlur }) => (
            <InputText
              dark={true}
              placeholder="louis.pasteur@gmail.com"
              keyboardType="email-address"
              value={value}
              error={error}
              hideError={true}
              onChangeText={onChange}
              onBlur={onBlur}
            />
          )}
        </Field>

        <Space height={24} />

        <Button status={signInResult} onPress={handleSubmit}>
          {t("signin.submit")}
        </Button>
      </form>

      <Space height={24} />

      <Box role="separator" direction="row" alignItems="center">
        <View style={styles.orDivider} />
        <Space width={24} />
        <Text variant="smallMono">{t("common.or")}</Text>
        <Space width={24} />
        <View style={styles.orDivider} />
      </Box>

      <Space height={24} />

      <OAuthSignInButton provider="google" redirectTo={oAuthredirectTo}>
        {t("signin.connect_with_google")}
      </OAuthSignInButton>
      <Space height={12} />
      <OAuthSignInButton provider="microsoft" redirectTo={oAuthredirectTo}>
        {t("signin.connect_with_microsoft")}
      </OAuthSignInButton>
      <Space height={12} />
      <OAuthSignInButton provider="github" redirectTo={oAuthredirectTo}>
        {t("signin.connect_with_github")}
      </OAuthSignInButton>
    </>
  );
};

type RetryOtpButtonProps = {
  onPress: () => void;
};

const RetryOtpButton: React.FC<RetryOtpButtonProps> = ({ onPress }) => {
  const [timer, setTimer] = useState(60);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : prev));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handlePress = () => {
    setTimer(60);
    onPress();
  };

  return (
    <Pressable disabled={timer > 0} onPress={handlePress}>
      <Text variant="small" color={colors.primary[500]}>
        {t("signin_verify.resend")}
        {timer > 0 && ` (${timer})`}
      </Text>
    </Pressable>
  );
};

type OtpFormProps = {
  signInRessource: SignInResource;
  setActive: (params: SetActiveParams) => Promise<void>;
  email: string;
  preFilledOtp?: string;
};

const OtpForm: React.FC<OtpFormProps> = ({
  signInRessource,
  setActive,
  email,
  preFilledOtp = "",
}) => {
  const { mutate: signIn, data: signInResult } = usePromiseMutation(
    sendOtpCode(signInRessource, setActive),
  );

  const { Field, submitForm } = useForm({
    otp: {
      initialValue: preFilledOtp,
    },
  });

  const handleSubmit = () =>
    submitForm(
      (values) => {
        if (hasDefinedKeys(values, ["otp"])) {
          signIn(values.otp);
        }
      },
      ({ otp }) => {
        if (otp != null) {
          toast({
            variant: "error",
            title: t("signin_verify.error.empty.title"),
            message: t("signin_verify.error.empty.description"),
          });
        }
      },
    );

  const resendOtp = () => {
    const hasSignUp = true;
    signInUser(signInRessource, email, hasSignUp);
  };

  useEffect(() => {
    if (signInRessource.status === "needs_first_factor" && preFilledOtp !== "") {
      handleSubmit();
    }
    // we need to run this effect only on mount
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (signInRessource.status !== "needs_first_factor") {
    return <Redirect to={router.SigninIndex()} replace />;
  }

  return (
    <>
      <Text variant="title3" color={invariantColor.white}>
        {t("signin_verify.title")}
      </Text>
      <Space height={8} />
      <Text variant="small">{t("signin_verify.description")}</Text>

      <Space height={24} />

      <Box direction="row" alignItems="center">
        <Field name="otp">
          {({ value, onChange }) => (
            <>
              <InputOtp
                nbDigits={6}
                value={value}
                invalid={false}
                disabled={signInResult.isLoading()}
                autofocus={true}
                onChange={onChange}
                onComplete={handleSubmit}
              />
            </>
          )}
        </Field>

        {signInResult.isLoading() && (
          <>
            <Space width={16} />
            <ActivityIndicator size="small" color={colors.primary[500]} />
          </>
        )}
      </Box>

      <Space height={24} />

      <Text variant="smallFlat">{t("signin_verify.not_received")}</Text>
      <Space height={4} />
      <RetryOtpButton onPress={resendOtp} />
    </>
  );
};

type SigninPageProps = {
  fromApp: ApplicationKey | undefined;
  redirectTo: string | undefined;
};

export const SigninPage: React.FC<SigninPageProps> = ({ fromApp, redirectTo }) => {
  useTitle(t("page_title.signin"), fromApp);

  const route = router.useRoute(["SigninIndex", "SigninVerification", "SigninSsoCallback"]);
  const { signIn: signInRessource, setActive, isLoaded } = useSignIn();
  const { isSignedIn } = useAuth();

  if (!isLoaded) {
    return <LoadingPage />;
  }

  if (isSignedIn) {
    return <Redirect to={redirectTo ?? router.Index()} replace />;
  }

  if (route?.name === "SigninSsoCallback") {
    return (
      <AuthenticateWithRedirectCallback
        signInFallbackRedirectUrl={redirectTo}
        signUpFallbackRedirectUrl={redirectTo}
      />
    );
  }

  return (
    <FullScreenScrollView
      paddingHorizontal={24}
      paddingVertical={24}
      maxWidth={470}
      verticalCenter={0.5}
    >
      <Tile style={styles.container}>
        {match(route)
          .with({ name: "SigninIndex" }, () => (
            <EmailForm
              fromApp={fromApp}
              signInRessource={signInRessource}
              oAuthredirectTo={redirectTo}
              onSuccess={(email) =>
                router.push("SigninVerification", {
                  email,
                  from_app: fromApp,
                  redirect_to: redirectTo,
                })
              }
            />
          ))
          .with(
            { name: "SigninVerification", params: { email: P.string } },
            ({ params: { email, otp } }) => (
              <OtpForm
                signInRessource={signInRessource}
                setActive={setActive}
                email={email}
                preFilledOtp={otp}
              />
            ),
          )
          .otherwise(() => (
            <Redirect to={router.SigninIndex()} replace />
          ))}
      </Tile>
    </FullScreenScrollView>
  );
};
